/**
 * API Configuration
 *
 * Centralized configuration for backend API base URL and public website URL.
 * Base URL defaults are defined in shared/domain/appConfig.ts
 */

import {
  SUIKAMON_API_URL,
  SUIKAMON_PUBLIC_URL as SUIKAMON_BASE_URL,
} from '@shared/domain/appConfig';

/**
 * Backend API base URL, resolved at build time in this order:
 *
 *   1. `VITE_API_URL`      — the canonical name (`.env.example`, Dockerfile, docker-compose).
 *   2. `VITE_API_BASE_URL` — the name README.md / CLAUDE.md / docs/SECRETS_MANAGEMENT.md
 *      documented for a long time. Accepted as an alias so an `.env` written from those docs
 *      still produces a working app.
 *   3. `SUIKAMON_API_URL`  — the production backend, the same default the Dockerfile bakes in.
 *
 * This deliberately does NOT throw when the variable is unset. It used to, at module scope,
 * and that ran while the entry chunk was still being evaluated — before
 * `ReactDOM.createRoot().render()` — so React never mounted and the <ErrorBoundary> never got
 * a chance to catch it. One misspelled env var therefore rendered a completely blank page on
 * every route, home included, with nothing but a cryptic console message to go on. A config
 * typo must degrade to a warning, never take the whole app down.
 *
 * Falling back to localhost is still avoided: the fallback is the production backend, so a
 * bundle built without the variable talks to a real API rather than a dev machine.
 */
const configuredApiUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL;

if (!configuredApiUrl) {
  console.warn(
    '[apiConfig] Neither VITE_API_URL nor VITE_API_BASE_URL is set; ' +
    `falling back to ${SUIKAMON_API_URL}. Set VITE_API_URL at build time to target another backend.`
  );
}

export const API_BASE_URL: string = configuredApiUrl || SUIKAMON_API_URL;

/**
 * Public website URL for Suikamon
 * Can be overridden by VITE_PUBLIC_URL environment variable
 * Used for social sharing, canonical URLs, etc.
 */
export const SUIKAMON_PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || SUIKAMON_BASE_URL;

/**
 * Pagination configuration
 */
export const PAGINATION = {
  RANKINGS_PAGE_SIZE: 250, // Items per page in rankings/leaderboard
} as const;
